import { useMutation, useQuery } from "./urqlHooks";
import { useSelectedCompanyId } from "./tenantCompanyId";

const GET_FLOW = `
query GET_FLOW($companyId: String!, $currency: String!, $view: String, $showProjections: Boolean, $onlyEstimations: Boolean) {
	getFlow(companyId: $companyId, currency: $currency, view: $view, showProjections: $showProjections, onlyEstimations: $onlyEstimations) {
    id
	  companyId
    name
    primaryView
    pastMonthsView
    futureMonthsView
    periods {
      start
      end
      isPast
      view
      showAsDisabled
    }
    report { 
      INCOME {
        label
        cashflowCategoryId
        oldCategoryKey
        isIncome
        isVisible
        fields {
          label
          cashflowAreaId
          isIncome
          isVisible
          isHidden
          values {
            fieldValue
            start
            end
            currency
            flowView
            isLate
            showAsDisabled
            isProjected
            isPast
            isTotal
            documents
          }
        }
        values {
          fieldValue
          start
          end
          currency
          flowView
          showAsDisabled
          isLate
          isProjected
          isPast
          isTotal
          documents
        }
      }
      EXPENSE {
        label
        cashflowCategoryId
        oldCategoryKey
        isIncome
        isVisible
          fields {
            label
            cashflowAreaId
            isIncome
            isVisible
            isHidden
              values {
                fieldValue
                start
                end
                currency
                flowView
                isLate
                showAsDisabled
                isProjected
                isPast
                isTotal
                documents
            }
        }
        values {
          fieldValue
          start
          end
          currency
          flowView
          isLate
          showAsDisabled
          isProjected
          isPast
          isTotal
          documents
        }
      }
      RESUME {
        label
        cashflowCategoryId
        oldCategoryKey
        isIncome
        fields {
          label
          cashflowAreaId
          isIncome
            values {
              fieldValue
              start
              end
              currency
              flowView
              isLate
              showAsDisabled
              isProjected
              isPast
              isTotal
              documents
          }
        }
				values {
          fieldValue
          start
          end
          currency
          flowView
          isLate
          isProjected
          showAsDisabled
          isPast
          isTotal
          documents
        }
      }
      TOTAL {
        label
        cashflowCategoryId
        oldCategoryKey
        isIncome
        fields {
          label
          cashflowAreaId
          isIncome
            values {
              fieldValue
              start
              end
              currency
              flowView
              isLate
              showAsDisabled
              isProjected
              isPast
              isTotal
              documents
          }
        }
        values {
          fieldValue
          start
          end
          currency
          flowView
          isLate
          showAsDisabled
          isProjected
          isPast
          isTotal
          documents
        }
      }
    }
	}
}
`;

const GET_FLOW_STRUCTURE = `
query GET_FLOW_STRUCTURE($companyId: String!) {
	getFlowStructure(companyId: $companyId) {
  id
	companyId
    name
    report { 
      INCOME {
        label
        cashflowCategoryId
        oldCategoryKey
        isIncome
        isDefault
        isVisible
        isHidden
        fields {
          label
          cashflowAreaId
          isIncome
          isDefault
          isVisible
          isHidden
        }
      }
      EXPENSE {
        label
        cashflowCategoryId
        oldCategoryKey
        isIncome
        isDefault
        isVisible
        isHidden
        fields {
          label
          cashflowAreaId
          isIncome
          isDefault
          isVisible
          isHidden
        }
      }
    }
	}
}
`;

const GET_FLOW_COMMENTS = `
  query GET_FLOW_COMMENTS($flowId: ID!, $companyId: String!) {
    getFlowComments(flowId: $flowId, companyId: $companyId) {
      user {
        id
        name
        email
      }
      date
      comment
    }
  }
`;

const CREATE_FLOW_COMMENT = `
  mutation CREATE_FLOW_COMMENT(
    $flowId: ID!
    $companyId: String!
    $userId: String!
    $userName: String
    $userEmail: String
    $date: DateTime!
    $comment: String!
  ) {
    createFlowComment(
      companyId: $companyId
      input: {
        flowId: $flowId
        user: {
          id: $userId
          name: $userName
          email: $userEmail
        }
        date: $date
        comment: $comment
      }
    ) {
      id
      innerComments {
        user {
          id
          name
          email
        }
        date
        comment
      }
    }
  }
`;

const CREATE_FLOW_FIELD = `
mutation CREATE_FLOW_FIELD(
  $companyId: String!,
  $id: String!,
  $fieldLabel: String!,
  $cashflowCategoryId: String!,
) {
	createFlowField(
    companyId: $companyId,
    id: $id,
    fieldLabel: $fieldLabel,
    cashflowCategoryId: $cashflowCategoryId,
  ) {
    status
    message
    data
	}
}
`;

const CREATE_FLOW_CATEGORY = `
mutation CREATE_FLOW_CATEGORY(
  $companyId: String!,
  $id: String!,
  $label: String!,
  $isIncome: Boolean!,
  $newFieldLabel: String,
  $importedFields: [String!]!,

) {
	createFlowCategory(
    companyId: $companyId,
    id: $id,
    label: $label,
    isIncome: $isIncome,
    newFieldLabel: $newFieldLabel,
    importedFields: $importedFields,
  ) {
    status
    message
    data
	}
}
`;



const EDIT_FLOW_CATEGORY = `
mutation EDIT_FLOW_CATEGORY(
  $companyId: String!,
  $id: String!,
  $cashflowCategoryId: String!,
  $newLabel: String,
  $importedFields: [String!]!,
) {
	editFlowCategory(
    companyId: $companyId,
    id: $id,
    cashflowCategoryId: $cashflowCategoryId,
    newLabel: $newLabel,
    importedFields: $importedFields,
  ) {
    status
    message
    data
	}
}
`;

const UPDATE_CATEGORY_ORDER = `
mutation UPDATE_CATEGORY_ORDER(
  $companyId: String!,
  $id: String!,
  $input: UpdateCategoryOrderInput!,
) {
  updateCategoryOrder(
    companyId: $companyId,
    id: $id,
    input: $input
  ) {
    status
    message
    data
  }
}
`;

export const UpdateFlowCategoryOrder = () => {
  const [result, executeMutation] = useMutation(UPDATE_CATEGORY_ORDER);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
}

const UPDATE_AREA_ORDER = `
mutation UPDATE_AREA_ORDER(
  $companyId: String!,
  $id: String!,
  $input: UpdateAreaOrderInput!,
) {
  updateAreaOrder(
    companyId: $companyId,
    id: $id,
    input: $input
  ) {
    status
    message
    data
  }
}
`;

export const UpdateFlowAreaOrder = () => {
  const [result, executeMutation] = useMutation(UPDATE_AREA_ORDER);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
}

const DELETE_FLOW_CATEGORY = `
mutation DELETE_FLOW_CATEGORY(
  $companyId: String!,
  $id: String!,
  $cashflowCategoryId: String!,
) {
	deleteFlowCategory(
    companyId: $companyId,
    id: $id,
    cashflowCategoryId: $cashflowCategoryId,
  ) {
    status
    message
    data
	}
}
`;

const EDIT_FLOW_FIELD = `
mutation EDIT_FLOW_FIELD(
  $companyId: String!,
  $id: String!,
  $newLabel: String,
  $cashflowAreaId: String!,
  $newCashflowCategoryId: String,
  $isHidden: Boolean
) {
	editFlowField(
    companyId: $companyId,
    id: $id,
    newLabel: $newLabel,
    cashflowAreaId: $cashflowAreaId,
    newCashflowCategoryId: $newCashflowCategoryId,
    isHidden: $isHidden
  ) {
    status
    message
    data
	}
}
`;

const FLOW_FIELD_DETAIL = `
query FLOW_FIELD_DETAIL(
  $companyId: String!,
  $pointIds: [String!]!,
) {
	getFlowAreaDetail(
    companyId: $companyId,
    pointIds: $pointIds,
  ) {
    totalAmount
    totalLength
    lateAmount
    lateLength
    settledAmount
    settledLength
    documents {
      id
      companyId
      name
      isLate
      dueDate
      documentDate
      documentType
      currencyType
      description
      amount
      cashflowAreaId
      recurringId
      paymentDate
      meta {
        ... on TaxDocumentMeta {
          factoring {
            cedido
          }
          isPaid
          isReceived
          issuerId
          issuerName
          receiverId
          receiverName
          documentId
          downloadURL
          download {
            pdf {
              url
            }
            xml {
              url
            }
            html {
              url  
            }
          }
          remaining
          originalAmount
          documentCode
          isReceived
          documentId
          summary
          nominaStatus {
            totalNomina
            totalSelected
            totalApproved
            inNominaProcess {
              id
              userSelected
              amount
              status
              dateSelected
              nameSelector
              emailSelector
              userToCreated
              nominaId
              dateToCreated
              nominaName
              userInformer
              dateInformed
              userToBank
              dateToBank
              userApproveReject
              dateApproveReject
              isMatched
            }
          }
          description {
            item
            name
            description
            quantity
            unit
            unit_price
            total_price
            discount
            discount_percentage
          }
          matchs {
            inMatch {
              id
              settleId
              origin
              type
              amount
              selected
              obligationType
              userRelated {
                userId
                firstName
                lastName
                date
              }
            }
            outMatch {
              id
              amount
              selected
              userRelated {
                userId
                firstName
                lastName
                date
              }
            }
          }
        }
      }

      meta {
        ... on ObligationMeta {
          isPaid
          isReceived
          issuerName
          issuerId
          receiverId
          receiverName
          documentId
          number
          typeLabel
          remaining
          originalAmount
          amount
          type
          isReceived
          tipo
          nominaStatus {
            totalNomina
            totalSelected
            totalApproved
            inNominaProcess {
              id
              userSelected
              amount
              status
              dateSelected
              nameSelector
              emailSelector
              userToCreated
              nominaId
              dateToCreated
              nominaName
              userInformer
              dateInformed
              userToBank
              dateToBank
              userApproveReject
              dateApproveReject
              isMatched
            }
          }
          detail {
            description
            item
            quantity
            unit_price
            total_price
            name
          }
          matchs {
            inMatch {
              id
              settleId
              origin
              type
              amount
              selected
              userRelated {
                userId
                firstName
                lastName
                date
              }
            }
            outMatch {
              id
              amount
              selected
              userRelated {
                userId
                firstName
                lastName
                date
              }
            }
          }
        }
      }

      meta {
        ... on CreditsMeta {
          installments
          interest
          stock
          totalPayments
          paymentNumber
          isPaid
          identificator
          period
          flexiblePeriod
          remaining
          matchs {
            inMatch {
              id
              settleId
              origin
              type
              amount
              selected
              userRelated {
                userId
                firstName
                lastName
                date
              }
            }
            outMatch {
              id
              amount
              selected
              userRelated {
                userId
                firstName
                lastName
                date
              }
            }
          }
        }
      }
    }
	}
}
`;

export const GetFlow = ({ companyId, currency, view, showProjections, onlyEstimations }) => {
  const [result, reexecuteFlow] = useQuery({
    query: GET_FLOW,
    variables: { companyId, currency, view, showProjections, onlyEstimations },
    requestPolicy: 'cache-and-network',
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteFlow };
}

export const GetFieldDetail = ({ companyId, pointIds }) => {
  const [result, reexecuteFlow] = useQuery({
    query: FLOW_FIELD_DETAIL,
    variables: { companyId, pointIds },
    requestPolicy: 'cache-and-network',
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteFlow };
}

export const GetFlowStructure = ({ companyId }) => {
  const [result, reexecuteFlow] = useQuery({
    query: GET_FLOW_STRUCTURE,
    variables: { companyId },
    pause: !companyId, // Don't execute if companyId is missing
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteFlow };
}

export const GetFlowComments = ({ flowId, companyId, pause = true }) => {
  const selectedCompanyId = useSelectedCompanyId();
  const resolvedCompanyId = companyId || selectedCompanyId;
  const [result, reexecuteQuery] = useQuery({
    query: GET_FLOW_COMMENTS,
    variables: { flowId, companyId: resolvedCompanyId },
    pause: pause || !flowId || !resolvedCompanyId,
    requestPolicy: 'network-only',
  });
  const { data: _data, fetching: _fetching, error: _error } = result;
  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    reexecuteQuery: () => reexecuteQuery({ requestPolicy: 'network-only' }),
  };
};

export const CreateFlowComment = () => {
  const [result, executeMutation] = useMutation(CREATE_FLOW_COMMENT);
  return {
    data: result.data,
    fetching: result.fetching,
    error: result.error,
    executeMutation: (variables) => executeMutation(variables),
  };
};

export const CreateFlowField = () => {
  const [result, executeMutation] = useMutation(CREATE_FLOW_FIELD);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
}

export const EditFlowField = () => {
  const [result, executeMutation] = useMutation(EDIT_FLOW_FIELD);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
}

export const CreateFlowCategory = () => {
  const [result, executeMutation] = useMutation(CREATE_FLOW_CATEGORY);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
}

export const EditFlowCategory = () => {
  const [result, executeMutation] = useMutation(EDIT_FLOW_CATEGORY);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
}



export const DeleteFlowCategory = () => {
  const [result, executeMutation] = useMutation(DELETE_FLOW_CATEGORY);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
}



const GET_EXCEL_CASHFLOW = `mutation GET_EXCEL_CASHFLOW($companyId: String!, $currency: String!, $view: String, $showProjections: Boolean) {
  CashFlowAsExcel(companyId: $companyId , currency : $currency, view : $view, showProjections : $showProjections)
}`

export const GetExcelCashFlow = () => {
  const [result, executeMutation] = useMutation(GET_EXCEL_CASHFLOW);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
}
